/* eslint-disable react/prop-types */
// This repo carries no prop-types dependency and no component declares them;
// disabling the rule here keeps the lint gate at zero new problems without
// adding a dependency for one file.
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from '@/components/utils/translations';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { TooltipProvider, Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { ArrowUp, ArrowDown, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { fetchReplacementCyclePage } from '@/lib/api/procurementServer';
import {
  hardwareKey, HARDWARE_LABEL, bandsFor, bandRangeLabel, replacementBandColor,
  REPLACEMENT_BANDS, REPLACEMENT_BAND_LABEL_KEYS,
} from '@/lib/replacementCycle';
import { getIncumbentSentimentColor, canonicalizeSentiment } from '@/utils/procurementDealUtils';
import { translateIncumbentSentiment } from '@/lib/accountClassificationLabels';
import DealValueDisplay from './DealValueDisplay';

const PAGE = 50;

const fmtDate = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  return Number.isNaN(dt.getTime()) ? '' : dt.toLocaleDateString();
};

/**
 * [2026-11-03] Contracts Intelligence for HARDWARE tenants (bibliotheca, niryo, positivo, jpik,
 * verypc): installed kit by age, oldest first. Filtering, sorting and paging run server-side
 * against the precomputed store (IT Hardware is 77k deals). The contract-expiry view
 * (CompetitorIntelligence) stays one click away via `renderExpiryView`.
 */
export default function ReplacementCycleView({
  tenantId, categories = [], controlledFilters = {}, search = '', onOpenDetail, renderExpiryView,
}) {
  const { t, language } = useTranslation();
  // One category per view: "IT Hardware" and "IT Hardware (Education only)" are the same key.
  const hwKey = useMemo(() => [...new Set((categories || []).map(hardwareKey).filter(Boolean))][0] || null, [categories]);
  const [view, setView] = useState('replacement');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [bands, setBands] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: 'age', direction: 'descending' });
  const [page, setPage] = useState(1);

  const f = controlledFilters || {};
  const query = useMemo(() => ({
    category: hwKey ? HARDWARE_LABEL[hwKey] : null,
    countries: f.countries || [], vendors: f.vendors || [], types: f.institutionTypes || [],
    sentiments: f.sentiments || [], bands, search,
    sort: sortConfig.key, dir: sortConfig.direction === 'ascending' ? 'asc' : 'desc',
  }), [hwKey, f.countries, f.vendors, f.institutionTypes, f.sentiments, bands, search, sortConfig]);
  const queryKey = JSON.stringify(query);
  useEffect(() => { setPage(1); }, [queryKey]);

  useEffect(() => {
    if (!tenantId || !hwKey || view !== 'replacement') return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchReplacementCyclePage(tenantId, { ...query, page, pageSize: PAGE })
      .then((d) => { if (!cancelled) { setData(d); setLoading(false); } })
      .catch((e) => { if (!cancelled) { setError(e.message); setLoading(false); } });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, queryKey, page, view]);

  // A selection with no hardware category in it has nothing to show here.
  if (!hwKey || view === 'expiry') {
    return (
      <div className="space-y-3">
        {hwKey ? <ViewToggle view={view} setView={setView} t={t} /> : null}
        {renderExpiryView?.()}
      </div>
    );
  }

  const b = data?.bands_years || bandsFor(hwKey);
  const k = data?.kpis || {};
  const bandCounts = data?.band_counts || {};
  const slice = data?.rows || [];
  const total = Number(data?.total) || 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE));

  const Sortable = ({ k: key, children, className = '' }) => {
    const on = sortConfig.key === key;
    return (
      <TableHead
        onClick={() => setSortConfig((p) => ({
          key,
          // Age opens oldest-first; everything else A→Z.
          direction: p.key === key
            ? (p.direction === 'ascending' ? 'descending' : 'ascending')
            : (key === 'age' ? 'descending' : 'ascending'),
        }))}
        className={`cursor-pointer hover:bg-gray-50 select-none align-top whitespace-normal max-w-0 pr-2 ${className}`}
      >
        <div className="flex items-start gap-1 min-w-0">
          <span className="break-words">{children}</span>
          {on && (sortConfig.direction === 'ascending' ? <ArrowUp className="w-3 h-3 shrink-0 mt-0.5" /> : <ArrowDown className="w-3 h-3 shrink-0 mt-0.5" />)}
        </div>
      </TableHead>
    );
  };

  return (
    <TooltipProvider delayDuration={100}>
      <div className="space-y-5">
        <ViewToggle view={view} setView={setView} t={t} />
        {loading && data ? (
          <div className="h-1 w-full rounded bg-[#00C2C2]/30 overflow-hidden" role="status">
            <div className="h-full bg-[#00C2C2] animate-pulse w-2/3 rounded" />
          </div>
        ) : null}

        {loading && !data ? (
          <div className="py-12 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#00C2C2]" /></div>
        ) : error ? (
          <div className="py-12 text-center text-red-600 text-sm">{error}</div>
        ) : (
          <>
            <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
              <Kpi label={t('procRcKpiInstalled')} value={k.installs} sub={t('procRcKpiInstalledSub', { n: Number(data?.deals ?? 0).toLocaleString() })} />
              <Kpi label={t('procRcKpiWindow')} value={k.in_window} sub={t('procRcSincePurchase', { n: b.plan })} tone="text-[#C8860D]" />
              <Kpi label={t('procRcKpiDue')} value={k.due_or_over} sub={t('procRcSincePurchase', { n: b.due })} tone="text-[#D94C3D]" />
              <Kpi label={t('procRcKpiOwn')} value={k.own_ageing} sub={t('procRcOwnYearsOld', { n: b.plan })} />
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs font-medium text-gray-600 mr-1">{t('procRcWindowFilter')}</span>
              {REPLACEMENT_BANDS.map((band) => {
                const on = bands.includes(band);
                return (
                  <button
                    key={band}
                    type="button"
                    onClick={() => setBands((p) => (on ? p.filter((x) => x !== band) : [...p, band]))}
                    className={`rounded-full border px-2.5 py-1 text-xs ${on ? 'ring-2 ring-[#00C2C2] ' : ''}${replacementBandColor(band)}`}
                    aria-pressed={on}
                  >
                    {t(REPLACEMENT_BAND_LABEL_KEYS[band])} · {bandRangeLabel(band, b, t)} ({Number(bandCounts[band] || 0).toLocaleString()})
                  </button>
                );
              })}
              {bands.length ? (
                <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setBands([])}>{t('clearFilters')}</Button>
              ) : null}
            </div>

            <div className="border rounded-lg overflow-x-auto -mx-2 px-2">
              <Table className="table-fixed w-full min-w-[1040px]">
                <colgroup>
                  {/* institution · country · vendor · product · installed · age · window · sentiment · value = 100% */}
                  <col className="w-[17%]" /><col className="w-[9%]" /><col className="w-[14%]" /><col className="w-[15%]" />
                  <col className="w-[9%]" /><col className="w-[7%]" /><col className="w-[11%]" /><col className="w-[9%]" />
                  <col className="w-[9%]" />
                </colgroup>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <Sortable k="account_name">{t('institution')}</Sortable>
                    <Sortable k="country">{t('country')}</Sortable>
                    <Sortable k="supplier">{t('vendor')}</Sortable>
                    <Sortable k="product_name">{t('product')}</Sortable>
                    <Sortable k="installed_on" className="text-xs">{t('procRcInstalled')}</Sortable>
                    <Sortable k="age">{t('procRcAge')}</Sortable>
                    <TableHead className="pr-2 align-top whitespace-normal">{t('procRcWindow')}</TableHead>
                    <Sortable k="sentiment">{t('sentiment')}</Sortable>
                    <TableHead className="pr-2 align-top">{t('value')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {slice.length === 0 ? (
                    <TableRow><TableCell colSpan={9} className="text-center py-8 text-gray-500">{t('noRecordsSelectedFilters')}</TableCell></TableRow>
                  ) : slice.map((i) => {
                    const s = canonicalizeSentiment(i.incumbent_sentiment);
                    return (
                      <TableRow key={i.install_key}>
                        <TableCell
                          className="font-medium cursor-pointer hover:text-[#00C2C2] pr-2 align-top whitespace-normal break-words max-w-0"
                          onClick={() => onOpenDetail?.({ deal_id: i.deal_id })}
                        >
                          {i.account_name}
                        </TableCell>
                        <TableCell className="text-xs pr-2 align-top whitespace-normal break-words max-w-0">{i.country}</TableCell>
                        <TableCell className="text-xs pr-2 align-top whitespace-normal break-words max-w-0">
                          {i.supplier}
                          {/* A distributor with no named maker sits in both fields; "X via X" says nothing. */}
                          {i.reseller && i.reseller !== i.supplier ? <div className="text-[10px] text-gray-500 mt-0.5">{t('procRcVia', { reseller: i.reseller })}</div> : null}
                        </TableCell>
                        <TableCell className="text-xs pr-2 align-top whitespace-normal break-words max-w-0">{i.product_name || ''}</TableCell>
                        <TableCell className="text-xs text-gray-600 pr-2 align-top whitespace-normal break-words max-w-0">
                          {fmtDate(i.installed_on)}
                          {i.system_purchases > 1 ? <div className="text-[10px] text-gray-500 mt-0.5">{t('procRcPurchases', { n: i.system_purchases })}</div> : null}
                        </TableCell>
                        <TableCell className="text-xs pr-2 align-top whitespace-normal max-w-0">
                          {i.age_years == null ? '' : (
                            <span title={i.age_is_floor ? t('procRcFloorHelp') : undefined}>
                              {i.age_is_floor ? '≥ ' : ''}{t('procRcYears', { n: Math.floor(i.age_years) })}
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="pr-2 align-top whitespace-normal max-w-0">
                          <Badge variant="outline" className={`text-xs border max-w-full whitespace-normal break-words ${replacementBandColor(i.band)}`}>
                            {t(REPLACEMENT_BAND_LABEL_KEYS[i.band])}
                          </Badge>
                        </TableCell>
                        <TableCell className="pr-2 align-top whitespace-normal max-w-0">
                          {/* Shown for every row: an ended contract does not end the kit or the view of it. */}
                          {s === 'unknown' ? null : (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="inline-block max-w-full cursor-help">
                                  <Badge variant="outline" className={`text-xs border max-w-full whitespace-normal break-words ${getIncumbentSentimentColor(i.incumbent_sentiment)}`}>
                                    {translateIncumbentSentiment(language, i.incumbent_sentiment)}
                                  </Badge>
                                </span>
                              </TooltipTrigger>
                              <TooltipContent side="top" className="max-w-[280px] text-xs whitespace-pre-line">
                                {i.incumbent_sentiment_rationale || t('procNoRationaleProvided')}
                              </TooltipContent>
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell className="text-xs pr-2 align-top whitespace-normal break-words max-w-0">
                          <DealValueDisplay deal={i} />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>

            {totalPages > 1 ? (
              <div className="flex items-center justify-center gap-2">
                <Button variant="ghost" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="gap-1">
                  <ChevronLeft className="h-4 w-4" />{t('previous')}
                </Button>
                <span className="text-xs text-gray-500">{page} / {totalPages}</span>
                <Button variant="ghost" size="sm" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="gap-1">
                  {t('next')}<ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            ) : null}
            <p className="text-xs text-gray-500 text-center">
              {t('procRcShowing', { shown: total.toLocaleString(), total: Number(k.installs || 0).toLocaleString() })}
            </p>
          </>
        )}
      </div>
    </TooltipProvider>
  );
}

function ViewToggle({ view, setView, t }) {
  return (
    <div className="inline-flex rounded-lg border bg-white p-0.5 text-sm" role="tablist">
      {[['replacement', 'procRcViewReplacement'], ['expiry', 'procRcViewExpiry']].map(([k, label]) => (
        <button
          key={k}
          type="button"
          role="tab"
          aria-selected={view === k}
          onClick={() => setView(k)}
          className={`px-3 py-1.5 rounded-md ${view === k ? 'bg-[#0A1A2F] text-white' : 'text-gray-600 hover:bg-gray-50'}`}
        >
          {t(label)}
        </button>
      ))}
    </div>
  );
}

function Kpi({ label, value, sub, tone }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={`mt-1 text-2xl font-semibold ${tone || 'text-foreground'}`}>{Number(value || 0).toLocaleString()}</div>
      {sub ? <div className="mt-0.5 text-xs text-muted-foreground">{sub}</div> : null}
    </div>
  );
}
